import type { LabeledGraphicFontSchemaType } from '@vedalib/editor/lib/brand'
import {
  CONFIG_ELEMENTS,
  type LabeledGraphicPoint,
  type LabeledGraphicElementValue,
} from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { FocusTrap } from 'focus-trap-react'
import { useCallback } from 'react'

import ButtonStyled from 'components/Editor/EditorElements/courseKit/DropdownMenuCourse/styled/ButtonStyled'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import Icon from 'components/uiKit/Icon'
import type { ElementFontCss } from 'services/Branding/types'
import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { extendTestData } from 'utils/test/qaData'

import { useLabeledGraphicContext } from '../../LabeledGraphicContext'
import PointTooltipStyled from '../../styled/PointTooltipStyled'
import PointContent from './PointContent'
import * as s from './PointTooltip.module.scss'
import { usePointTooltipKeyboard } from './usePointTooltipKeyboard'
import { generateAccessibleTitleFromRichText } from './utils'

interface IPointTooltipProps {
  point: LabeledGraphicPoint
  index: number
  count: number
  value: LabeledGraphicElementValue
  id: string
  items?: LabeledGraphicPoint[]
  font: ElementFontCss<LabeledGraphicFontSchemaType>
  focusedElement?: HTMLElement | null
  onClose?: () => void
}

const PointTooltip = ({
  point,
  count,
  index,
  value,
  font,
  onClose,
  focusedElement,
}: IPointTooltipProps) => {
  const styles = useLabeledGraphicContext((c) => c.styles)
  const mode = useLabeledGraphicContext((c) => c.mode)
  const { id } = useLabeledGraphicContext((c) => c.element) || {}
  const { uuid = '' } = useLabeledGraphicContext((c) => c.block) || {}
  const tooltipId = `${id}-point-tooltip`
  const tooltipLabel = generateAccessibleTitleFromRichText(point.description, {
    index,
    total: count,
    imageAlt: point.image?.accessibility,
  })
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: uuid,
      elementType: CONFIG_ELEMENTS.labeledGraphic.type,
      elementId: id,
    },
  )
  const setActivePoint = useLabeledGraphicContext((c) => c.setActivePoint)
  const isMobile = mode.deviceMode === DeviceMode.mobile
  const isPreview = mode.previewMode === PreviewMode.preview
  const isFirst = index === 0
  const isLast = index === count - 1

  const currentItem = value.items[index]?.value
  const prevItem = value.items[index - 1]?.value
  const nextItem = value.items[index + 1]?.value

  const createSwitchData = useCallback(
    (toLabelId: string | undefined, toCurrent: number) =>
      entityAnalyticsConfig.switch({
        resultExtensions: {
          toLabelId,
          fromLabelId: currentItem,
          fromCurrent: index + 1,
          toCurrent,
          total: count,
        },
        objectExtensions: baseObjectExtensions,
        elementId: id,
      }),
    [currentItem, index, count, entityAnalyticsConfig, baseObjectExtensions, id],
  )

  const goPrev = useCallback(
    (e?: React.MouseEvent | KeyboardEvent) => {
      e?.stopPropagation()
      if (!isFirst) {
        setActivePoint(prevItem)
        sendXapiData(createSwitchData(prevItem, index))
      }
    },
    [isFirst, prevItem, sendXapiData, setActivePoint, createSwitchData, index],
  )

  const goNext = useCallback(
    (e?: React.MouseEvent | KeyboardEvent) => {
      e?.stopPropagation()
      if (!isLast) {
        setActivePoint(nextItem)
        sendXapiData(createSwitchData(nextItem, index + 2))
      }
    },
    [isLast, nextItem, sendXapiData, setActivePoint, createSwitchData, index],
  )

  const { rootRef } = usePointTooltipKeyboard({ goNext, goPrev, onClose })

  const descriptionId = `${id}-point-tooltip-description`

  return (
    <FocusTrap
      active={isPreview && styles.navigation.switch}
      focusTrapOptions={{
        setReturnFocus: focusedElement || false,
        allowOutsideClick: true,
      }}
    >
      <PointTooltipStyled
        aria-describedby={descriptionId}
        aria-label={tooltipLabel}
        aria-modal='true'
        className={s.root}
        id={tooltipId}
        ref={rootRef}
        role='dialog'
        style={styles.tooltip}
        tabIndex={-1}
      >
        <PointContent font={font} id={id} index={index} point={point} value={value} />
        {styles.navigation.switch && !isMobile && (
          <div className={cn(s.navigation, s[mode.deviceMode])} style={font?.navigation}>
            <div className={s.arrows}>
              <ButtonStyled
                aria-disabled={isFirst}
                aria-label='Previous'
                disabled={isFirst}
                onClick={goPrev}
              >
                <Icon
                  className={cn({ [s.disabled]: isFirst })}
                  name='iconsForElementsLeft'
                  testData={extendTestData({
                    index,
                    disabled: isFirst,
                  })}
                  aria-hidden
                />
              </ButtonStyled>
              <ButtonStyled
                aria-disabled={isLast}
                aria-label='Next'
                disabled={isLast}
                onClick={goNext}
              >
                <Icon
                  className={cn({ [s.disabled]: isLast })}
                  name='iconsForElementsRight'
                  testData={extendTestData({
                    index,
                    disabled: isLast,
                  })}
                  aria-hidden
                />
              </ButtonStyled>
            </div>
            <div>
              {index + 1}/{count}
            </div>
          </div>
        )}
        <ScreenReaderOnlyText id={descriptionId}>
          Press Escape to close. Use arrow buttons to move between markers.
        </ScreenReaderOnlyText>
      </PointTooltipStyled>
    </FocusTrap>
  )
}

export default PointTooltip
